//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Min max dialog
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   06.10.23 Sean Flook                  Use colour variables.
//    003   05.01.24 Sean Flook                  Use CSS shortcuts.
//    004   10.01.24 Sean Flook                  Fix warnings.
//    005   27.02.24 Sean Flook            MUL15 Fixed dialog title styling.
//    006   27.03.24 Sean Flook                  Added ADSDialogTitle.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    007   08.10.24 Sean Flook        IMANN-986 Added new error parameter.
//endregion Version 1.0.1.0
//region Version 1.0.5.0
//    008   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    009   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    010   24.02.26 Sean Flook       IMANN-2109 Display the warning message when setting min/max values.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { Dialog, DialogActions, DialogContent, Typography, Grid, TextField, Button } from "@mui/material";
import ADSDialogTitle from "../components/ADSDialogTitle";

import CloseIcon from "@mui/icons-material/Close";
import WidthFullIcon from "@mui/icons-material/WidthFull";
import WidthNormalIcon from "@mui/icons-material/WidthNormal";
import DoneIcon from "@mui/icons-material/Done";

import { blueButtonStyle, whiteButtonStyle } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

MinMaxDialog.propTypes = {
  variant: PropTypes.oneOf(["usrn", "uprn", "esu", "scale"]).isRequired,
  minValue: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
  maximum: PropTypes.number.isRequired,
  error: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  isWarning: PropTypes.bool,
  onNewMinMax: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

function MinMaxDialog({
  variant,
  minValue,
  maxValue,
  maximum,
  error,
  isOpen,
  isWarning = false,
  onNewMinMax,
  onClose,
}) {
  const theme = useTheme();

  const [showDialog, setShowDialog] = useState(false);
  const [minMaxType, setMinMaxType] = useState("unknown");
  const [newMin, setNewMin] = useState(null);
  const [newMax, setNewMax] = useState(null);

  /**
   * Event to handle when the dialog is closing.
   *
   * @param {object} event The event object.
   * @param {string} reason The reason why the dialog is closing.
   */
  const handleDialogClose = (event, reason) => {
    event.stopPropagation();
    if (reason === "escapeKeyDown") handleCancelClick();
  };

  /**
   * Event to handle when the full range button is clicked.
   */
  const handleFullRangeClick = () => {
    if (onNewMinMax) onNewMinMax({ variant: variant, min: Number(newMin), max: Number(newMax), type: "full" });
  };

  /**
   * Event to handle when the new range button is clicked.
   */
  const handleNewRangeClick = () => {
    if (onNewMinMax) onNewMinMax({ variant: variant, min: Number(newMin), max: Number(newMax), type: "new" });
  };

  /**
   * Event to handle when the done button is clicked.
   */
  const handleDoneClick = () => {
    if (onNewMinMax) onNewMinMax({ variant: variant, min: Number(newMin), max: Number(newMax), type: "done" });
  };

  /**
   * Event to handle when the cancel button is clicked.
   */
  const handleCancelClick = () => {
    if (onClose) onClose();
    else setShowDialog(false);
  };

  /**
   * Event to handle when the minimum value changes.
   *
   * @param {object} event The event object.
   */
  const onMinChangeEvent = (event) => {
    setNewMin(event.target.value);
  };

  /**
   * Event to handle when the maximum value changes.
   *
   * @param {object} event The event object.
   */
  const onMaxChangeEvent = (event) => {
    setNewMax(event.target.value);
  };

  useEffect(() => {
    setNewMin(minValue ? Number(minValue) : 0);
    setNewMax(maxValue ? Number(maxValue) : 0);
  }, [minValue, maxValue]);

  useEffect(() => {
    switch (variant) {
      case "usrn":
        setMinMaxType("USRN");
        break;

      case "uprn":
        setMinMaxType("UPRN");
        break;

      case "esu":
        setMinMaxType("ESU");
        break;

      case "scale":
        setMinMaxType("Scale");
        break;

      default:
        setMinMaxType("Unknown");
        break;
    }

    setShowDialog(isOpen);
  }, [variant, isOpen]);

  return (
    <Dialog open={showDialog} aria-labelledby="add-lookup-dialog" fullWidth maxWidth="xs" onClose={handleDialogClose}>
      <ADSDialogTitle
        title={`Update available ${minMaxType} range`}
        closeTooltip="Cancel"
        onClose={handleCancelClick}
      />
      <DialogContent sx={{ mt: theme.spacing(2) }}>
        <Grid container alignItems="center" rowSpacing={2}>
          <Grid size={4}>
            <Typography variant="body1" align="right" gutterBottom>
              Minimum
            </Typography>
          </Grid>
          <Grid size={8}>
            <TextField
              id={`${minMaxType.toLowerCase().replaceAll(" ", "-")}-min-control`}
              type="number"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              size="small"
              value={newMin}
              sx={{
                color: theme.palette.background.contrastText,
                pl: theme.spacing(1),
                pr: theme.spacing(1),
              }}
              onChange={onMinChangeEvent}
              slotProps={{
                htmlInput: { max: `${maximum}` },
              }}
            />
          </Grid>
          <Grid size={4}>
            <Typography variant="body1" align="right" gutterBottom>
              Maximum
            </Typography>
          </Grid>
          <Grid size={8}>
            <TextField
              id={`${minMaxType.toLowerCase().replaceAll(" ", "-")}-max-control`}
              type="number"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              size="small"
              value={newMax}
              sx={{
                color: theme.palette.background.contrastText,
                pl: theme.spacing(1),
                pr: theme.spacing(1),
              }}
              onChange={onMaxChangeEvent}
              slotProps={{
                htmlInput: { max: `${maximum}` },
              }}
            />
          </Grid>
          {error && (
            <Grid size={12}>
              <Typography variant="body1" align="left" color="error" gutterBottom>
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-start", mb: theme.spacing(1), ml: theme.spacing(3) }}>
        {variant !== "scale" && !isWarning && (
          <Button
            onClick={handleFullRangeClick}
            autoFocus
            variant="contained"
            sx={blueButtonStyle}
            startIcon={<WidthFullIcon />}
          >
            Full range
          </Button>
        )}
        {variant !== "scale" && !isWarning && (
          <Button
            onClick={handleNewRangeClick}
            autoFocus
            variant="contained"
            sx={blueButtonStyle}
            startIcon={<WidthNormalIcon />}
          >
            New range
          </Button>
        )}
        {variant === "scale" && !isWarning && (
          <Button onClick={handleDoneClick} autoFocus variant="contained" sx={blueButtonStyle} startIcon={<DoneIcon />}>
            Done
          </Button>
        )}
        {!isWarning && (
          <Button
            onClick={handleCancelClick}
            autoFocus
            variant="contained"
            sx={whiteButtonStyle}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
        )}
        {isWarning && (
          <Button
            onClick={handleCancelClick}
            autoFocus
            variant="contained"
            sx={whiteButtonStyle}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default MinMaxDialog;
